/**
 * Authentication Context for Thai Security Directory
 * Manages user authentication state, login, logout, and session handling
 */

import React, { createContext, useContext, useReducer, useEffect, useRef, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authService } from '../services/authService';
import { User, LoginCredentials, RegisterData, AuthResponse } from '../types/auth';

// Authentication State Types
interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;
}

// Authentication Actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: User }
  | { type: 'SET_LOADING'; payload: boolean };

// Authentication Context Interface
interface AuthContextType {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  token: string | null;

  // Actions
  login: (credentials: LoginCredentials) => Promise<User>;
  register: (data: RegisterData) => Promise<User>;
  logout: () => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  clearError: () => void;

  // Permission checks
  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  canManageCompanies: () => boolean;

  // 3-Role System helpers (boolean properties, not functions)
  isAdmin: boolean;
  isMember: boolean;
  isGuest: boolean;
}

// Initial State
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  token: localStorage.getItem('access_token'),
};

// Authentication Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case 'AUTH_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };

    case 'AUTH_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };

    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };

    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };

    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      };

    default:
      return state;
  }
};

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Authentication Provider Props
interface AuthProviderProps {
  children: ReactNode;
}

// Authentication Provider Component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const queryClient = useQueryClient();

  // Flag to prevent duplicate initialization in React.StrictMode
  const hasInitialized = useRef(false);

  // Initialize authentication on app start
  useEffect(() => {
    const initializeAuth = async () => {
      // Skip if already initialized (React.StrictMode double-mount protection)
      if (hasInitialized.current) {
        return;
      }

      hasInitialized.current = true;

      const token = localStorage.getItem('access_token');

      if (token) {
        try {
          // Verify token and get user info
          const user = await authService.getCurrentUser();
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user, token }
          });
        } catch (error) {
          // Token is invalid, clear it
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          dispatch({ type: 'AUTH_FAILURE', payload: 'Session expired' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, []);

  // Auto-refresh token before expiry
  useEffect(() => {
    if (state.isAuthenticated && state.token) {
      const refreshInterval = setInterval(async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.warn('Token refresh failed:', error);
          logout();
        }
      }, 25 * 60 * 1000); // Refresh every 25 minutes (tokens expire in 30 minutes)

      return () => clearInterval(refreshInterval);
    }
  }, [state.isAuthenticated, state.token]);

  // Login function
  const login = async (credentials: LoginCredentials): Promise<User> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await authService.login(credentials);

      // Store tokens
      localStorage.setItem('access_token', response.access_token);
      localStorage.setItem('refresh_token', response.refresh_token);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: response.user,
          token: response.access_token
        }
      });

      // Invalidate all queries to refetch with new authentication state
      // This ensures contact info and other auth-dependent data is refreshed
      queryClient.invalidateQueries();

      // Return user for role-based redirect
      return response.user;
    } catch (error: unknown) {
      // Extract error message
      // NOTE: axios interceptor (api.ts) transforms errors to standardError with .details property
      // Priority: error.details (from interceptor) > error.message (fallback)
      const standardError = error as {
        message?: string;
        details?: string;
        code?: string;
        type?: string;
        response?: { data?: { detail?: string; error?: { details?: string; message?: string }; message?: string } }
      };

      const errorMessage = standardError.details ||
                           standardError.response?.data?.error?.details ||
                           standardError.response?.data?.detail ||
                           standardError.response?.data?.error?.message ||
                           standardError.response?.data?.message ||
                           standardError.message ||
                           'เข้าสู่ระบบไม่สำเร็จ';

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Register function (returns user, no auto-login)
  const register = async (data: RegisterData): Promise<User> => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await authService.register(data);

      // Registration returns { message, user } but no tokens
      // User must login after registration
      dispatch({ type: 'SET_LOADING', payload: false });

      // Return user for success message
      return response.user;
    } catch (error: unknown) {
      // Extract error message
      // NOTE: axios interceptor (api.ts) transforms errors to standardError with .details property
      // Priority: error.details (from interceptor) > error.message (fallback)
      const standardError = error as {
        message?: string;
        details?: string;
        code?: string;
        type?: string;
        response?: { data?: { detail?: string; error?: { details?: string; message?: string }; message?: string } }
      };

      const errorMessage = standardError.details ||
                           standardError.response?.data?.error?.details ||
                           standardError.response?.data?.detail ||
                           standardError.response?.data?.error?.message ||
                           standardError.response?.data?.message ||
                           standardError.message ||
                           'การลงทะเบียนไม่สำเร็จ';

      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Logout function
  const logout = (): void => {
    console.log('[Logout] localStorage BEFORE:', { ...localStorage });

    // Call logout API (optional)
    authService.logout().catch(console.warn);

    // Clear ALL possible token keys from localStorage
    // Current keys
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    // Legacy keys (backward compatibility)
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Admin keys (if any)
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');

    console.log('[Logout] localStorage AFTER:', { ...localStorage });

    // Update state
    dispatch({ type: 'LOGOUT' });

    // Invalidate all queries to clear cached authenticated data
    queryClient.invalidateQueries();
  };

  // Update profile function
  const updateProfile = async (data: Partial<User>): Promise<void> => {
    try {
      const updatedUser = await authService.updateProfile(data);
      dispatch({ type: 'UPDATE_USER', payload: updatedUser });
    } catch (error: unknown) {
      // Get error message from backend response
      // Priority: detail (FastAPI) -> error.message (nested) -> message (top-level) -> fallback
      const axiosError = error as { response?: { data?: { detail?: string; error?: { message?: string }; message?: string }; status?: number } };
      const errorMessage = axiosError.response?.data?.detail ||
                           axiosError.response?.data?.error?.message ||
                           axiosError.response?.data?.message ||
                           'การอัปเดตโปรไฟล์ไม่สำเร็จ';

      // Only logout on authentication errors (401, 403)
      // Don't logout on validation errors (400)
      const statusCode = axiosError.response?.status;
      if (statusCode === 401 || statusCode === 403) {
        dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      } else {
        // For validation errors (400) and other errors, just set error without logging out
        dispatch({ type: 'CLEAR_ERROR' }); // Clear any existing errors first
      }

      throw error;
    }
  };

  // Change password function
  const changePassword = async (currentPassword: string, newPassword: string): Promise<void> => {
    try {
      await authService.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword: newPassword
      });
    } catch (error: unknown) {
      // Extract error message (same pattern as login)
      const axiosError = error as { response?: { data?: { detail?: string; error?: { message?: string }; message?: string } } };
      const errorMessage = axiosError.response?.data?.detail ||
                           axiosError.response?.data?.error?.message ||
                           axiosError.response?.data?.message ||
                           'การเปลี่ยนรหัสผ่านไม่สำเร็จ';
      dispatch({ type: 'AUTH_FAILURE', payload: errorMessage });
      throw error;
    }
  };

  // Refresh token function
  const refreshToken = async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await authService.refreshToken(refreshToken);

      // Update access token
      localStorage.setItem('access_token', response.access_token);

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          user: state.user!,
          token: response.access_token
        }
      });
    } catch (error) {
      // Refresh failed, logout user
      logout();
      throw error;
    }
  };

  // Clear error function
  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Permission helper functions
  const hasRole = (role: string): boolean => {
    return state.user?.role === role;
  };

  const hasPermission = (permission: string): boolean => {
    if (!state.user) return false;

    const permissionMap: { [key: string]: string[] } = {
      // Admin-only permissions
      'create_company': ['ADMIN', 'SUPER_ADMIN'],
      'edit_any_company': ['ADMIN', 'SUPER_ADMIN'],
      'delete_company': ['ADMIN', 'SUPER_ADMIN'],
      'verify_company': ['ADMIN', 'SUPER_ADMIN'],
      'manage_users': ['ADMIN', 'SUPER_ADMIN'],
      'ban_users': ['ADMIN', 'SUPER_ADMIN'],
      'view_admin_panel': ['ADMIN', 'SUPER_ADMIN'],
      'manage_system': ['SUPER_ADMIN'],
      'view_analytics': ['ADMIN', 'SUPER_ADMIN'],
      'approve_requests': ['ADMIN', 'SUPER_ADMIN'],

      // Member permissions (includes legacy roles)
      'claim_company': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'create_change_request': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'view_advanced_search': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
      'view_contact_info_owned': ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'],
    };

    const allowedRoles = permissionMap[permission] || [];
    return allowedRoles.includes(state.user.role);
  };

  const canManageCompanies = (): boolean => {
    if (!state.user) return false;
    // Legacy support: COMPANY_OWNER is now MEMBER with companies
    return ['MEMBER', 'USER', 'COMPANY_OWNER', 'ADMIN', 'SUPER_ADMIN'].includes(state.user.role);
  };

  // Compute role helpers (as boolean values, not functions)
  const isAdmin: boolean = state.user
    ? ['ADMIN', 'SUPER_ADMIN'].includes(state.user.role)
    : false;

  const isMember: boolean = state.user
    ? ['MEMBER', 'USER', 'COMPANY_OWNER'].includes(state.user.role) || isAdmin
    : false;

  const isGuest: boolean = !state.user;

  // Context value
  const value: AuthContextType = {
    // State
    user: state.user,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    error: state.error,
    token: state.token,

    // Actions
    login,
    register,
    logout,
    updateProfile,
    changePassword,
    refreshToken,
    clearError,

    // Permission checks
    hasRole,
    hasPermission,
    canManageCompanies,
    isAdmin,
    isMember,
    isGuest,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Higher-order component for protected routes
export const withAuth = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  return (props: P) => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated) {
      // Redirect to login page
      window.location.href = '/login';
      return null;
    }

    return <Component {...props} />;
  };
};

// Higher-order component for role-based access
export const withRole = <P extends object>(
  Component: React.ComponentType<P>,
  requiredRoles: string[]
): React.FC<P> => {
  return (props: P) => {
    const { user, isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (!isAuthenticated || !user) {
      window.location.href = '/login';
      return null;
    }

    if (!requiredRoles.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }

    return <Component {...props} />;
  };
};